import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    updateCartCount();
    window.addEventListener('storage', updateCartCount);
    window.addEventListener('cartUpdated', updateCartCount);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const updateCartCount = () => {
    const cart = JSON.parse(localStorage.getItem('serviceCart') || '[]');
    setCartCount(cart.length);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '100px', width: 'auto' }} />
        </Link>
        
        <ul className="navbar-menu">
          <li><Link to="/" className="navbar-link">Home</Link></li>
          <li><Link to="/services" className="navbar-link">Services</Link></li>
          <li><Link to="/gallery" className="navbar-link">Gallery</Link></li>
          <li><Link to="/booking" className="navbar-cta blink-animation">Book Now</Link></li>
        </ul>
        
        {/* Cart with Text - Before Hamburger */}
        <Link 
          to="/booking" 
          className="cart-with-label"
          style={{ 
            position: 'relative',
            marginLeft: '2rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'linear-gradient(135deg, #d4a574, #c89b64)',
            borderRadius: '8px',
            textDecoration: 'none',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 165, 116, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ 
            color: 'white', 
            fontWeight: '600',
            fontSize: '1rem',
            letterSpacing: '0.5px'
          }} className="blink-animation">
            Cart
          </span>
          <div style={{ position: 'relative' }}>
            <ShoppingCart size={20} color="white" />
            {cartCount > 0 && (
              <span className="cart-badge-red" style={{
                background: '#ff4444',
                color: 'white',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: '700',
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                border: '2px solid white'
              }}>
                {cartCount}
              </span>
            )}
          </div>
        </Link>
        
        <button 
          className={`mobile-menu-btn ${mobileMenuOpen ? 'hidden' : ''}`}
          onClick={toggleMobileMenu}
        >
          ☰
        </button>
      </div>
      
      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'mobile-menu-open' : ''}`} onClick={closeMobileMenu}>
        <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
          <div className="mobile-menu-header">
            <img src="/cleo-logo.png" alt="Cleo Professionals" style={{ height: '60px', width: 'auto' }} />
            <button className="mobile-menu-close" onClick={closeMobileMenu}>✕</button>
          </div>
          <ul className="mobile-menu-list">
            <li><Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>HOME</Link></li>
            <li><Link to="/services" className="mobile-menu-link" onClick={closeMobileMenu}>Services</Link></li>
            <li><Link to="/gallery" className="mobile-menu-link" onClick={closeMobileMenu}>Gallery</Link></li>
            <li>
              <Link to="/booking" className="mobile-menu-link" onClick={closeMobileMenu} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
                <ShoppingCart size={18} className="blink-animation" />
                Cart
                {cartCount > 0 && (
                  <span className="cart-badge-red" style={{
                    background: '#ff4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    marginLeft: '0.25rem'
                  }}>
                    {cartCount}
                  </span>
                )}
              </Link>
            </li>
            <li><Link to="/booking" className="mobile-menu-link mobile-menu-cta blink-animation" onClick={closeMobileMenu}>Book Now</Link></li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      
      {/* Header Section */}
      <section style={{
        paddingTop: '120px',
        paddingBottom: '60px',
        background: 'linear-gradient(135deg, #fef5e7 0%, #f9f0e8 100%)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
          <span className="section-badge">Gallery</span>
          <h1 className="section-title" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            Our Work Showcase
          </h1>
          <p className="section-subtitle">
            Browse through our collection of stunning transformations
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section style={{ padding: '4rem 2rem', background: '#fafafa' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1.5rem'
            }}>
              {images.map((image) => (
                <div
                  key={image.id}
                  data-testid={`gallery-image-${image.id}`}
                  onClick={() => setSelectedImage(image)}
                  style={{
                    position: 'relative',
                    borderRadius: '15px',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    height: '300px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <img
                    src={image.image_url}
                    alt={image.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '1rem',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                    color: 'white'
                  }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'white' }}>
                      {image.title}
                    </h3>
                    <p style={{ fontSize: '0.875rem', opacity: 0.9, textTransform: 'capitalize' }}>
                      {image.category}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          data-testid="lightbox-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={() => setSelectedImage(null)}
        >
          <button
            data-testid="close-lightbox"
            style={{
              position: 'absolute',
              top: '2rem',
              right: '2rem',
              background: 'white',
              border: 'none',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem'
            }}
            onClick={() => setSelectedImage(null)}
          >
            ×
          </button>
          <div style={{ maxWidth: '90%', maxHeight: '90%' }}>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.title}
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: '10px',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
              }}
            />
            <div style={{ textAlign: 'center', marginTop: '1rem', color: 'white' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: 'white' }}>
                {selectedImage.title}
              </h3>
              <p style={{ fontSize: '1rem', opacity: 0.8, textTransform: 'capitalize' }}>
                {selectedImage.category}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
